/**
 * Shared TypeScript interfaces for Sales/OrderManagement components
 */

import type { Order, Salesperson } from '@api/orders';
import type { CurrentUser } from '@/react/lib/capabilities';
import type { FilterCategory } from '@components/filters';

// Re-export Order and Salesperson for convenience
export type { Order, Salesperson };

/** Order statuses the list may be filtered by */
export const ORDER_STATUS_FILTER_VALUES = ['lead', 'confirmed', 'cancelled'] as const;
export type OrderStatus = (typeof ORDER_STATUS_FILTER_VALUES)[number];

/** Translations structure for OrderManagement */
export interface OrderManagementTranslations {
  toolbar?: ToolbarTranslations;
  table?: TableTranslations;
}

/** Translations for OrdersToolbar */
export interface ToolbarTranslations {
  salesperson?: string;
  new_order?: string;
  search_placeholder?: string;
}

/** Translations for OrdersTable */
export interface TableTranslations {
  order_number?: string;
  customer?: string;
  source?: string;
  events?: string;
  amount?: string;
  date?: string;
  actions?: string;
  no_results?: string;
  loading?: string;
  view_more?: string;
  view_less?: string;
}

/** Props for OrdersToolbar section component */
export interface OrdersToolbarProps {
  /** Filter categories rendered by the Filter by dropdown */
  categories: FilterCategory[];
  /** Callback when New Order button is clicked */
  onNewOrder: () => void;
  /** Translation overrides */
  translations?: ToolbarTranslations;
  /** Additional CSS classes */
  className?: string;
}

/** Props for OrdersTable section component */
export interface OrdersTableProps {
  /** Orders to display */
  orders: Order[];
  /** Loading state (first load) */
  isLoading?: boolean;
  /** Fetching state (background refetch) */
  isFetching?: boolean;
  /** Row click callback */
  onRowClick?: (order: Order) => void;
  /** Order ID click callback (opens detail sheet) */
  onOrderIdClick?: (orderId: number) => void;
  /** Signed-in user — gates the row action menu. */
  currentUser?: CurrentUser;
  /** Additional CSS classes */
  className?: string;
}

/** Pagination state for the mobile card list pager */
export interface OrdersCardListPagination {
  /** Current page (1-indexed) */
  page: number;
  /** Total number of pages */
  totalPages: number;
  /** Callback when the page changes */
  onPageChange: (page: number) => void;
}

/** Props for OrdersCardList section component (mobile rendering of the table) */
export interface OrdersCardListProps {
  /** Orders to display */
  orders: Order[];
  /** Loading state (first load) */
  isLoading?: boolean;
  /** Fetching state (background refetch) */
  isFetching?: boolean;
  /** Order ID click callback (opens detail sheet) */
  onOrderIdClick?: (orderId: number) => void;
  /** Pager rendered after the cards when there is more than one page */
  pagination?: OrdersCardListPagination;
  /** Translation overrides */
  translations?: TableTranslations;
  /** Additional CSS classes */
  className?: string;
}

/** Props for OrdersMobileHeader section component */
export interface OrdersMobileHeaderProps {
  /** Page title (status header label) */
  title: string;
  /** Formatted order count for the title badge */
  count: string;
  /** Current search input value */
  searchValue: string;
  /** Callback when the search input changes (cleared on search close) */
  onSearchChange: (value: string) => void;
  /** Search input placeholder */
  searchPlaceholder: string;
  /** Search input aria-label */
  searchAriaLabel: string;
  /** Opens the app-shell mobile navigation drawer */
  onOpenNavigation?: () => void;
  /** Additional CSS classes */
  className?: string;
}

/** Translations for the OrdersFilterSheet filter menu */
export interface FilterMenuTranslations {
  /** Sheet title, e.g. "Filter" */
  title?: string;
  /** Salesperson row title (reuses toolbar.salesperson) */
  salespersonLabel?: string;
  /** Salesperson row subtitle */
  salespersonSubtitle?: string;
  /** ARIA label for the sheet's close button */
  closeAriaLabel?: string;
}

/** Props for OrdersFilterSheet section component */
export interface OrdersFilterSheetProps {
  /** Whether the sheet is open */
  open: boolean;
  /** Callback when the sheet open state changes */
  onOpenChange: (open: boolean) => void;
  /** Callback when the "Filter by salesperson" row is tapped (closes this sheet, opens the salesperson sheet) */
  onOpenSalespersonFilter: () => void;
  /** Translation overrides */
  translations?: FilterMenuTranslations;
}

/** Props for SalespersonFilterSheet section component */
export interface SalespersonFilterSheetProps {
  /** Whether the sheet is open */
  open: boolean;
  /** Callback when the sheet open state changes */
  onOpenChange: (open: boolean) => void;
  /** Available salespersons */
  salespersons: Salesperson[];
  /** Currently selected salesperson IDs */
  selectedIds: string[];
  /** Callback when the selection changes (applies immediately) */
  onSelectionChange: (ids: string[]) => void;
  /** Sheet title */
  title?: string;
  /** Clear all button label */
  clearAllLabel?: string;
  /** Confirm button label */
  confirmLabel?: string;
}

/** Empty state variants */
export type EmptyStateVariant = 'empty' | 'no-results' | 'error';

/** Props for OrdersEmptyState section component */
export interface OrdersEmptyStateProps {
  /** Which variant to display */
  variant: EmptyStateVariant;
  /** Search query (for no-results variant) */
  searchQuery?: string;
  /** Error object (for error variant) */
  error?: Error;
  /** Retry callback (for error variant) */
  onRetry?: () => void;
  /** Clear filters callback (for no-results variant) */
  onClearFilters?: () => void;
  /** Additional CSS classes */
  className?: string;
}

/** Props for AllOrdersPage container component (stateless) */
export interface AllOrdersPageProps {
  /** Total order count for display */
  totalCount?: number;
  /** Available salespersons for filter */
  salespersons: Salesperson[];
  /** Currently selected salesperson IDs */
  selectedSalespersonIds?: string[];
  /** Callback when salesperson filter changes */
  onSalespersonChange: (ids: string[]) => void;
  /** Currently selected order statuses */
  selectedOrderStatuses?: string[];
  /** Callback when the order status filter changes */
  onOrderStatusesChange: (values: string[]) => void;
  /** Callback when New Order button is clicked */
  onNewOrder: () => void;
  /** Translation overrides */
  translations?: OrderManagementTranslations;
  /** Whether data is loading for the first time */
  isLoading?: boolean;
  /** Whether data is being refetched (e.g., when filters change) */
  isFetching?: boolean;

  /** Orders to display in the data table */
  orders?: Order[];
  /** Row click callback */
  onRowClick?: (order: Order) => void;
  /** Order ID click callback (opens detail sheet) */
  onOrderIdClick?: (orderId: number) => void;
  /** Controlled selected order ID for the detail sheet */
  selectedOrderId?: number | null;
  /** Callback when the detail sheet selection changes */
  onSelectedOrderChange?: (orderId: number | null) => void;
  /** Signed-in user, forwarded to the order detail sheet for capability gating */
  currentUser?: CurrentUser;
  /** Pagination for the mobile card list pager */
  pagination?: OrdersCardListPagination;
}

export interface OrdersMobileFooterProps {
  /** Salesperson filter trigger label */
  salespersonLabel?: string;
  /** Callback when the salesperson filter trigger is tapped */
  onOpenSalespersonFilter: () => void;
  /** Additional CSS classes */
  className?: string;
}
